import React, { useRef, useState } from 'react';

const INBOX_DRAG_TYPE = 'application/x-mindmappi-inbox-idea';

export { INBOX_DRAG_TYPE };

const stopCanvasEvent = (event) => event.stopPropagation();

function InboxIcon({ size = 18 }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 5h16v14H4z" />
      <path d="M4 14h4l2 2h4l2-2h4" />
    </svg>
  );
}

function InboxCard({
  node,
  branchCount,
  isMobile,
  canWrite,
  onDelete,
  onDragStart,
  onDragEnd,
}) {
  const canDragToMap = canWrite && !isMobile;
  const cardStatus = branchCount > 0
    ? `${branchCount} ${branchCount === 1 ? 'child' : 'children'}`
    : node.creatorName || 'Captured idea';

  return (
    <article
      className="idea-inbox__card"
      draggable={canDragToMap}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(INBOX_DRAG_TYPE, node.id);
        event.dataTransfer.setData('text/plain', `mindmappi-inbox:${node.id}`);
        onDragStart?.(node.id);
      }}
      onDragEnd={() => onDragEnd?.()}
      data-inbox-idea-id={node.id}
      title={canDragToMap ? 'Drag onto an idea in the map' : undefined}
    >
      <div className="idea-inbox__card-text">{node.text}</div>
      {!!node.tags?.length && (
        <div className="idea-inbox__tags">
          {node.tags.map((tag) => <span key={tag}>#{tag}</span>)}
        </div>
      )}
      <footer>
        <span>{canDragToMap && <svg role="img" aria-label="Draggable idea" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" style={{ verticalAlign: 'middle', marginRight: 6 }}>
          {[3, 7, 11].flatMap(y => [4, 9].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" />))}
        </svg>}{cardStatus}</span>
        {canWrite && (
          <div>
            <button type="button" onClick={() => onDelete(node.id)} aria-label="Delete inbox idea">
              ×
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}

export default function IdeaInbox({
  mode,
  isMobile,
  mobileView,
  isDarkMode,
  inbox,
  captureTarget,
  canWrite,
  isDropActive,
  onModeChange,
  onMobileViewChange,
  onAddIdea,
  onDeleteIdea,
  onInboxDragStart,
  onInboxDragEnd,
}) {
  const [draft, setDraft] = useState('');
  const [info, setInfo] = useState('');
  const [draftTarget, setDraftTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const composerRef = useRef(null);
  const effectiveTarget = (draft || info) ? draftTarget : captureTarget;

  const updateDraft = (value) => {
    if (!draft && !info && value) setDraftTarget(captureTarget || null);
    if (!value && !info) setDraftTarget(null);
    setDraft(value);
  };

  const submit = async () => {
    if (!draft.trim() || submitting || !canWrite) return;
    setSubmitting(true);
    try {
      const added = await onAddIdea(draft, effectiveTarget?.id || null, info);
      if (added === false) return;
      setInfo('');
      setDraft('');
      setDraftTarget(null);
      window.requestAnimationFrame(() => composerRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  const mobileTabs = isMobile && (
    <nav className="idea-inbox__mobile-tabs" data-export-ignore="true" data-viewport-occlusion="true">
      <button
        type="button"
        className={mobileView === 'ideas' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('ideas')}
      >
        Ideas {inbox.count > 0 && <span>{inbox.count}</span>}
      </button>
      <button
        type="button"
        className={mobileView === 'map' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('map')}
      >
        Map
      </button>
    </nav>
  );

  const expanded = (isMobile && mobileView === 'ideas') || mode === 'expanded';
  const showPanel = expanded && (!isMobile || mobileView === 'ideas');
  const showBubble = !showPanel && (
    mode === 'collapsed' || (isMobile && mobileView === 'map' && mode !== 'hidden')
  );

  return (
    <>
      {mobileTabs}
      {showBubble && (
        <button
          type="button"
          className={`idea-inbox__bubble ${isDropActive ? 'is-drop-active' : ''}`}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onClick={() => {
            onModeChange('expanded');
            if (isMobile) onMobileViewChange('ideas');
          }}
          aria-label={`Open idea inbox with ${inbox.count} ${inbox.count === 1 ? 'idea' : 'ideas'}`}
        >
          <InboxIcon />
          <span>Inbox</span>
          <strong>{inbox.count}</strong>
        </button>
      )}
      {showPanel && (
        <aside
          className={`idea-inbox__panel ${isMobile ? 'is-mobile' : ''} ${isDropActive ? 'is-drop-active' : ''}`}
          data-dark={isDarkMode ? 'true' : 'false'}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onPointerDown={stopCanvasEvent}
          onMouseDown={stopCanvasEvent}
          onClick={stopCanvasEvent}
        >
          <header className="idea-inbox__header">
            <div>
              <InboxIcon size={20} />
              <span>
                <strong>{effectiveTarget ? `Ideas for ${effectiveTarget.text || 'Untitled'}` : 'Idea inbox'}</strong>
                {!effectiveTarget && <small>{`${inbox.count} unplaced ${inbox.count === 1 ? 'idea' : 'ideas'}`}</small>}
              </span>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  onModeChange('collapsed');
                  if (isMobile) onMobileViewChange('map');
                }}
                title="Collapse inbox"
                aria-label="Collapse idea inbox"
              >
                −
              </button>
              <button
                type="button"
                onClick={() => {
                  onModeChange('hidden');
                  if (isMobile) onMobileViewChange('map');
                }}
                title="Hide inbox"
                aria-label="Hide idea inbox"
              >
                ×
              </button>
            </div>
          </header>

          <div className="idea-inbox__composer">
            <label htmlFor="inbox-idea-title">Title</label>
            <textarea
              id="inbox-idea-title"
              ref={composerRef}
              value={draft}
              disabled={!canWrite}
              onChange={(event) => updateDraft(event.target.value)}
              onKeyDown={(event) => {
                const isComposing = event.isComposing || event.nativeEvent?.isComposing;
                if (event.key !== 'Enter' || event.shiftKey || isComposing) return;
                event.preventDefault();
                submit();
              }}
              placeholder={canWrite ? 'Jot down an idea…' : 'You have view-only access'}
              rows={2}
            />
            <label htmlFor="inbox-idea-info">Info <small>(optional)</small></label>
            <textarea
              id="inbox-idea-info"
              value={info}
              disabled={!canWrite}
              maxLength={20000}
              onChange={(event) => {
                const value = event.target.value;
                if (!draft && !info && value) setDraftTarget(captureTarget || null);
                if (!draft && !value) setDraftTarget(null);
                setInfo(value);
              }}
              placeholder="Add a description or notes…"
              rows={3}
            />
            <div>
              <span>
                {effectiveTarget ? `Adding under ${effectiveTarget.text || 'selected idea'}` : 'Adding to the inbox'}
              </span>
              <button type="button" disabled={!draft.trim() || submitting || !canWrite} onClick={submit}>
                {submitting ? 'Adding…' : 'Add idea'}
              </button>
            </div>
          </div>

          <div className="idea-inbox__list">
            {inbox.rootNodes.map((node) => (
              <InboxCard
                key={node.id}
                node={node}
                branchCount={inbox.branchCountByRootId.get(node.id) || 0}
                isMobile={isMobile}
                canWrite={canWrite}
                onDelete={onDeleteIdea}
                onDragStart={onInboxDragStart}
                onDragEnd={onInboxDragEnd}
              />
            ))}
            {!inbox.rootNodes.length && (
              <div className="idea-inbox__empty">
                <InboxIcon size={24} />
                <strong>Your inbox is clear</strong>
                <span>Capture a thought without deciding where it belongs yet.</span>
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
